// ./src/components/generic/EventCurrencyField/EventCurrencyField.tsx
//
// THE EVENT'S CURRENCY on an amount (#A4.7a / #A4.7b, owner 2026-09-18/19: "do not allow to change currency but show
// it as suffix/prefix"). An event item has no currency of its own — the server reads the event's and its inputs no
// longer take one — so an amount is shown IN the event's currency, not beside a currency field:
//
//   • `useCurrencyAffixes(code?)` → `leftSection` (the symbol, when it is a pure sign like $ € £) + `rightSection` (the
//     ISO code, always) for a NumberInput. `MoneyInput` uses it too, so every amount reads the same way.
//   • `CatalogCurrencyWarning` — only when the item was filled in from an ACCOUNT catalog entry priced in another
//     currency (a sponsor package, an artist, a rental asset): the amount is used as it is, nothing converts it.
//
// The host changes the currency in ONE place, Event details (and only until money is recorded).
import React from "react";
import { Alert, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CURRENCY_MAP, getCurrencyDisplayLabel } from "@/config/currencyOptions";
import { useCurrentEvent } from "@/contexts";
import { AppIcon } from "../AppIcon";

/** "CA$ CAD - Canadian Dollar", or just the code when it is not in the catalog. */
export const currencyDisplay = (code?: string | null): string => {
  const option = code ? CURRENCY_MAP[code] : undefined;

  return option ? getCurrencyDisplayLabel(option) : code || "—";
};

/** A symbol worth showing before the number: a pure sign (€ £ ₹) — "US$" / "CA$" / "CHF" would repeat the code. */
const signOf = (code: string): string | null => {
  const symbol = CURRENCY_MAP[code]?.symbol;

  return symbol && !/[A-Za-z]/.test(symbol) ? symbol : null;
};

export interface CurrencyAffixes {
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  rightSectionPointerEvents?: "none";
}

/** Prefix / suffix props for an amount input, in `code` (a currency code) — nothing when there is none. */
export const currencyAffixes = (code?: string | null): CurrencyAffixes => {
  const upper = (code || "").trim().toUpperCase();

  if (!upper) {
    return {};
  }

  const sign = signOf(upper);

  return {
    leftSection: sign ? (
      <Text component="span" size="md" c="dimmed">
        {sign}
      </Text>
    ) : undefined,
    rightSection: (
      <Text component="span" size="md" c="dimmed" fw={500}>
        {upper}
      </Text>
    ),
    rightSectionWidth: 52,
    rightSectionPointerEvents: "none",
  };
};

/** The affixes for the EVENT's currency — `code` when given, else the current event's. */
export const useCurrencyAffixes = (code?: string | null): CurrencyAffixes => {
  const { currentEvent } = useCurrentEvent();

  return currencyAffixes(code ?? currentEvent?.currencyCode ?? null);
};

export interface CatalogCurrencyWarningProps {
  /** The event's currency — defaults to the current event's. */
  currencyCode?: string | null;
  /** The currency of the catalog entry this item was filled in from. */
  sourceCurrencyCode?: string | null;
  /** What that entry is ("This package", "This act"…). */
  sourceLabel?: string;
}

/** Renders ONLY when the catalog entry is priced in another currency than the event — otherwise nothing. */
export const CatalogCurrencyWarning: React.FC<CatalogCurrencyWarningProps> = ({ currencyCode, sourceCurrencyCode, sourceLabel }) => {
  const { t } = useTranslation(["common"]);
  const { currentEvent } = useCurrentEvent();
  const code = (currencyCode ?? currentEvent?.currencyCode ?? null)?.toUpperCase() ?? null;
  const source = sourceCurrencyCode ? sourceCurrencyCode.toUpperCase() : null;

  if (!code || !source || source === code) {
    return null;
  }

  return (
    <Alert color="orange" variant="light" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
      {t("common:currency.sourceDiffers", {
        defaultValue: "{{what}} is priced in {{source}}. On this event the amount is charged in {{event}} as it is — it is not converted.",
        what: sourceLabel ?? t("common:currency.sourceDefault", { defaultValue: "The catalog entry" }),
        source,
        event: code,
      })}
    </Alert>
  );
};
